import type { QueryHandlers, QueryName } from "./queryHandlers";
import { encodeArgs } from "./wire";

/** A failed /api/q call, with the HTTP status so a caller can tell 401 from the rest. */
export class QueryHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "QueryHttpError";
  }
}

/**
 * Calls a read registered in queryHandlers through POST /api/q/[name]. Same
 * arguments and result as the action it wraps, but it does not go through the
 * Server Action queue. Throws an Error with the server's message, so the company
 * access check (isCompanyAccessDeniedError) keeps working.
 *
 * Type-only import of the registry: no server code reaches the bundle.
 */
export async function callQuery<N extends QueryName>(
  name: N,
  ...args: Parameters<QueryHandlers[N]>
): Promise<Awaited<ReturnType<QueryHandlers[N]>>> {
  const response = await fetch(`/api/q/${name}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: encodeArgs(args),
  });

  if (!response.ok) {
    let message = `Query "${name}" failed (${response.status})`;
    try {
      const body: unknown = await response.json();
      const text = (body as { error?: unknown } | null)?.error;
      if (typeof text === "string" && text.length > 0) message = text;
    } catch {
      // Non-JSON body (a proxy or platform error page): keep the status message.
    }
    throw new QueryHttpError(message, response.status);
  }

  return (await response.json()) as Awaited<ReturnType<QueryHandlers[N]>>;
}
